import { ExceptionJson } from "./API/ExceptionJson";
import { TsConfig } from "./API/TsConfig";
import {
  findFirstSupportedLanguageWithRegionAgnosticFallbackOrDefault,
  getUserPreferredBrowserLanguages,
} from "./Localization";
import { SessionRoute } from "./SessionRoute";
import { loadPublicErrorCss } from "./StaticAssets";
import * as StaticConfig from "./StaticConfig";
import { VizqlStorage } from "./Storage";
import { TableauLocalizedStrings, loadStrings } from "./Strings";
import { getQueryFlag } from "./UserShowOptions";
import { PathConfig, VizqlPaths } from "./VizqlPaths";
import {
  openEptMetricsContext,
  EptBreakdownMetricNames,
  closeEptMetricsContext,
  uuidToHex32,
} from "./telemetry/TelemetryBreakdown";
import { ErrorDialog } from "./views/ErrorDialog";
import { showErrorPage } from "./views/Errors/ErrorPage";
import {
  exceptionFrom,
  showInstantAnalyticsErrorPageWithException,
  showInstantAnalyticsErrorPageWithLocalizedErrorMessage,
} from "./views/Errors/InstantAnalyticsErrorPage";
import { showMaintenancePage } from "./views/Errors/MaintenancePage";
import { showMissingViewPage } from "./views/Errors/MissingViewPage";
import { showMobileAuthoringUnsupportedPage } from "./views/Errors/MobileAuthoringUnsupportedPage";
import { showPublicErrorPage } from "./views/Errors/PublicErrorPage";

const redirectGuard = ":redirect";
const preventRedirectLoop = "auth";

const skipSmartRoutingParam = ":skipSmartRouting";

function createStringsFromErrorMessage(
  errorMessage: string
): TableauLocalizedStrings {
  return {
    getString: (_key: string) => errorMessage,
    AuthoringWorkbookTitle: (_workbookName: string) => errorMessage,
    EmbeddedWorkbookTitle: (_workbookName: string) => errorMessage,
    ErrorPageHelpOnline: (_communityLink: string) => errorMessage,
    MaintenanceMessage: (_serverName: string) => errorMessage,
    PublicErrorBrowseGallery: (_link: string) => errorMessage,
    InstantAnalyticsGenericExceptionMessage: (_dataProvider: string) =>
      errorMessage,
  };
}

function removeRedirectGuard() {
  const url = new URL(window.location.href);
  const param = url.searchParams.get(redirectGuard);

  if (param) {
    url.searchParams.delete(redirectGuard);
    window.history.replaceState(window.history.state, "", url.href);
  }
}

export async function startSession(
  routingKey: SessionRoute | null,
  authToken: string | null,
  doc: Document,
  location: Location | null
): Promise<TsConfig | null> {
  openEptMetricsContext(EptBreakdownMetricNames.prepareStartSession);
  const headers: HeadersInit = {
    Accept: "application/json",
    // Not really used but some firewalls require a Content-Type for all post requests so we added this
    "Content-Type": "application/x-www-form-urlencoded",
  };
  // The referrer-policy may truncate the 'referer' to the content to the origin
  // in cross-domain embedding scenarios. Send the viz content URL to
  // the server so it can reflect back a reasonably formatted signin-page with
  // the path parameter that allows vizportal to redirect back to the current page

  // Add :redirect=auth querystring parameter to URL to allow us to detect redirect cycles
  const tableauVizLocation = new URL(location?.href || doc.location.href);
  tableauVizLocation.searchParams.set(redirectGuard, preventRedirectLoop);
  headers["Tableau-Viz-Location"] = tableauVizLocation.href;
  // Add a global trace id for EPT telemetry. The header name is picked to align with Tracer. This id could potentially
  // be shared for other telemetry events in the same session. For now, it's just for EPT.
  // Spec: https://salesforce.quip.com/FZtRA8A7IW4N
  headers["X-B3-TraceID"] = uuidToHex32();
  // Add X-B3-SpanID such that Zipkin won't overwrite X-B3-TraceID. (See W-17431191 and W-17600580)
  headers["X-B3-SpanID"] = uuidToHex32();
  // This is a safe play. Without adding this header doesn't seem have any effect on whether Zipkin would overwrite
  // X-B3-TraceID. Per https://confluence.internal.salesforce.com/display/SERVMESH/Distributed+Tracing, add it to explicitly
  // tell upstream services to record the trace
  headers["X-B3-Sampled"] = "1";

  if (routingKey?.ingressProxySessionHeader) {
    headers["Global-Session-Header"] = routingKey.ingressProxySessionHeader;
  }
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  const vizqlPaths = getVizqlPaths(routingKey?.vizqlSessionRoute || null, doc);
  // we provide a way to avoid smartRouting generally only used for testing purposes
  if (!getQueryFlag(skipSmartRoutingParam)) {
    const vizPathHeaderValue =
      vizqlPaths.getVizPathHeaderValue(tableauVizLocation);

    if (vizPathHeaderValue) {
      headers["Tableau-Viz-Path"] = vizPathHeaderValue;
    } else {
      console.warn(
        "Unable to retrieve a valid viz path header value. Omitting Tableau-Viz-Path header."
      );
    }
  }

  closeEptMetricsContext(EptBreakdownMetricNames.prepareStartSession);
  openEptMetricsContext(EptBreakdownMetricNames.startSession);
  const response = await fetch(
    vizqlPaths.getStartSessionUrl(tableauVizLocation),
    {
      headers: headers,
      method: "POST",
    }
  );
  removeRedirectGuard();
  closeEptMetricsContext(EptBreakdownMetricNames.startSession);
  try {
    if (!response.ok || !response.body || response.redirected) {
      // Equivalent of legacy 'Promise.reject(response)'
      throw response;
    }

    const tsConfig = (await response.json()) as TsConfig;
    return addRoutingHeadersToTsConfig(tsConfig, response.headers, routingKey);
  } catch (reason) {
    void handleError(document, (href: string, doc: Document) =>
      doc.location.replace(href)
    )(response);
    return null;
  }
}

function addRoutingHeadersToTsConfig(
  tsConfig: TsConfig,
  responseHeaders: Headers,
  requested: SessionRoute | null
): TsConfig {
  const routingHeader = "Global-Session-Header";

  tsConfig.vizqlRoutingHeaderName = routingHeader;
  tsConfig.vizqlRoutingKey =
    responseHeaders.get(routingHeader) ||
    requested?.ingressProxySessionHeader ||
    "";
  return tsConfig;
}

function getVizqlPaths(
  vizqlSessionRoute: string | null,
  doc?: Document
): VizqlPaths {
  const isSalesforceNative =
    (doc && StaticConfig.isSalesforceNative(doc)) ?? false;
  const vizqlPrefix = StaticConfig.vizqlPrefix(doc);
  const vizqlRoot = isSalesforceNative
    ? vizqlPrefix
    : "/" + (vizqlPrefix || "vizql");
  const pathConfig: PathConfig = {
    isSalesforceNative,
    sessionId: vizqlSessionRoute,
    showPagePrefix: StaticConfig.showPagePathPrefix(doc) || undefined,
    vizqlRoot,
  };

  return new VizqlPaths(pathConfig);
}

function deferIfEmbedded(action: () => void): void {
  if (window.top && window.top !== window) {
    setTimeout(() => {
      action();
    }, 0);
  } else {
    action();
  }
}

export type VizLoadErrorOptions = {
  name: string;
  response: {
    redirected: boolean;
    status: number;
  };
};

export function notifyEmbeddingPage(response: Response, target: Window) {
  try {
    const responseSummary = {
      redirected: response.redirected,
      status: response.status,
    };
    target.postMessage(
      {
        name: "vizloaderror",
        response: responseSummary,
      },
      new URL(document.referrer).origin
    );
  } catch (error) {
    console.error(error);
  }
}

async function getExceptionJson(
  response: Response
): Promise<ExceptionJson | null> {
  const contentType = response.headers.get("content-type");

  if (contentType && contentType.includes("application/json")) {
    const json = (await response.json()) as ExceptionJson;
    return json;
  }

  return null;
}

async function handleBadRequest(
  response: Response,
  doc: Document,
  stringsLoaded: Promise<TableauLocalizedStrings>
) {
  const error = await getExceptionJson(response);
  const tableauUserErrorMessage = response.headers.get(
    "tableau_user_error_message"
  );
  let strings: TableauLocalizedStrings | undefined;
  let bodyStrings: TableauLocalizedStrings = createStringsFromErrorMessage("");
  let stringsLoadedSuccessfully = false;

  try {
    strings = await stringsLoaded;
    stringsLoadedSuccessfully = true;
    bodyStrings = tableauUserErrorMessage
      ? createStringsFromErrorMessage(tableauUserErrorMessage)
      : strings;
  } catch {
    console.log("Unable to load localized strings");
    if (!tableauUserErrorMessage) {
      return;
    }
    bodyStrings = createStringsFromErrorMessage(tableauUserErrorMessage);
  }

  if (error) {
    switch (error.type) {
      case "mobile-authoring-unsupported":
        showMobileAuthoringUnsupportedPage(doc, bodyStrings);
        return;
      case "instant-analytics-error":
        showInstantAnalyticsErrorPageWithLocalizedErrorMessage(
          doc,
          bodyStrings,
          error.localizedErrorMessage || "",
          error.pageTitle // The server sends back a localized string here
        );
        return;
      default:
        break;
    }
  }

  const errorDialog = new ErrorDialog(doc);
  if (stringsLoadedSuccessfully && strings) {
    errorDialog.overwriteTitle(strings.getString("ServerErrorRejected"));
  }
  errorDialog
    .overwriteBodyHTML(bodyStrings.getString("ServerErrorRejectedMessage"))
    .show();
}

async function handleServiceUnavailable(
  response: Response,
  doc: Document,
  url: URL,
  location: Location,
  stringsLoaded: Promise<TableauLocalizedStrings>,
  redirect: (s: string, doc: Document) => void,
  execute?: (action: () => void) => void
) {
  const refreshGuard = ":refresh";
  const preventRefreshLoop = "no";

  if (url.searchParams.get(refreshGuard) === preventRefreshLoop) {
    void showServerErrorDialog(response, stringsLoaded, doc);
    return;
  }

  const error = await getExceptionJson(response);
  const tableauUserErrorMessage = response.headers.get(
    "tableau_user_error_message"
  );
  let strings: TableauLocalizedStrings | undefined;
  let bodyStrings: TableauLocalizedStrings = createStringsFromErrorMessage("");

  if (error) {
    try {
      strings = await stringsLoaded;
      bodyStrings = tableauUserErrorMessage
        ? createStringsFromErrorMessage(tableauUserErrorMessage)
        : strings;
    } catch {
      console.log("Unable to load localized strings");
      if (!tableauUserErrorMessage) {
        return;
      }
      bodyStrings = createStringsFromErrorMessage(tableauUserErrorMessage);
    }

    const serverName = error.serverName || "";
    const details = error.details || [];
    const communityLink = error.communityLink || "";

    switch (error.type) {
      case "maintenance":
        showMaintenancePage(doc, bodyStrings, serverName);
        return;
      case "no-license":
        showErrorPage(
          doc,
          bodyStrings,
          details,
          communityLink,
          error.errorType
        );
        return;
      default:
        break;
    }
  }

  execute!(() => {
    new VizqlStorage(window.localStorage).removeLocation(location);
    url.searchParams.set(refreshGuard, preventRefreshLoop);
    redirect(url.href, doc);
  });
}

async function handleNotFound(
  response: Response,
  doc: Document,
  stringsLoaded: Promise<TableauLocalizedStrings>
) {
  // This is a cosmetically improved port of the Tableau Server monolith
  // workgroup/src/silos/tableau-server/services/war-vizql/war/WEB-INF/views/viewer/404.jsp
  // Except it removes some javascript looking for an ID= query param, which
  // doesn't seem to exist.

  // A better 404 message exists in vizportal, and includes a 'Back to content'
  // button. Maybe the navigation target for that button could be based on
  // server config, since Tableau Server should redirect to /#/explore,
  // and Chrysalis shouldn't.

  const tableauUserErrorMessage = response.headers.get(
    "tableau_user_error_message"
  );
  let strings: TableauLocalizedStrings | undefined;
  let bodyStrings: TableauLocalizedStrings = createStringsFromErrorMessage("");
  let stringsLoadedSuccessfully = false;

  try {
    strings = await stringsLoaded;
    stringsLoadedSuccessfully = true;
    bodyStrings = tableauUserErrorMessage
      ? createStringsFromErrorMessage(tableauUserErrorMessage)
      : strings;
  } catch {
    console.log("Unable to load localized strings");
    if (!tableauUserErrorMessage) {
      return;
    }
    bodyStrings = createStringsFromErrorMessage(tableauUserErrorMessage);
  }

  if (StaticConfig.isPublic(doc)) {
    await loadPublicErrorCss(doc);
    showPublicErrorPage(doc, bodyStrings);
  } else {
    const error = await getExceptionJson(response);

    if (error) {
      if (error.type === "missing-view") {
        showMissingViewPage(
          doc,
          bodyStrings,
          error.linksGallery,
          error.requestString
        );
        return;
      }
    }

    const errorDialog = new ErrorDialog(doc);
    if (stringsLoadedSuccessfully && strings) {
      errorDialog.overwriteTitle(
        strings.getString("ShowPageErrorNotFoundSummary")
      );
    }
    errorDialog
      .overwriteBody(bodyStrings.getString("ShowPageErrorNotFoundDetail"))
      .show();
  }
}

async function handleForbidden(
  response: Response,
  doc: Document,
  stringsLoaded: Promise<TableauLocalizedStrings>
) {
  const error = await getExceptionJson(response);

  if (error) {
    if (error.type === "trusted-ticked-failed") {
      const details = error.details || [];
      const communityLink = error.communityLink || "";

      const tableauUserErrorMessage = response.headers.get(
        "tableau_user_error_message"
      );
      let strings: TableauLocalizedStrings | undefined;
      let bodyStrings: TableauLocalizedStrings =
        createStringsFromErrorMessage("");

      try {
        strings = await stringsLoaded;
        bodyStrings = tableauUserErrorMessage
          ? createStringsFromErrorMessage(tableauUserErrorMessage)
          : strings;
      } catch {
        console.log("Unable to load localized strings");
        if (!tableauUserErrorMessage) {
          return;
        }
        bodyStrings = createStringsFromErrorMessage(tableauUserErrorMessage);
      }

      showErrorPage(doc, bodyStrings, details, communityLink, error.errorType);

      return;
    }
  }

  // Treat remaining 403 exceptions as if they were 404s
  void handleNotFound(response, doc, stringsLoaded);
  return;
}

export function handleError(
  doc: Document,
  redirect: (s: string, doc: Document) => void,
  execute?: (action: () => void) => void
): (response: Response) => Promise<void> {
  if (!execute) {
    execute = deferIfEmbedded;
  }
  return async (response: Response): Promise<void> => {
    // Let embedders handle this first, possibly by replacing the current iframe
    // or navigating it to a custom login experience.
    if (window.top && window.top !== window) {
      notifyEmbeddingPage(response, window.parent);
    }

    const responseLanguage =
      response.headers.get("Tableau-User-Language") ??
      response.headers.get("Content-Language");
    const preferredLanguages = getUserPreferredBrowserLanguages();
    if (responseLanguage !== null) {
      // Add response language to the front so we try to match it first
      preferredLanguages.unshift(new Intl.Locale(responseLanguage));
    }
    const language =
      findFirstSupportedLanguageWithRegionAgnosticFallbackOrDefault(
        preferredLanguages
      );

    const stringsLoaded = loadStrings(doc, language);
    const location = doc.location || new URL(doc.baseURI);
    if (
      location.pathname.startsWith("/vizql/showInstantAnalytics") &&
      showInstantAnalyticsException(
        response.status,
        stringsLoaded,
        doc,
        location.pathname
      )
    ) {
      return;
    }
    if (response.redirected) {
      if (response.url === location.origin + "/") {
        // Some client redirected to the (ancient, pre-vizportal) /views URI
        // which Apache helpfully redirected to /#/views. When that redirect
        // is handled by a browser GET request for an html page, it loads
        // the vizportalclient, and loads the viz-client as an embedded component.
        // However, because this was a fetch request, we got redirected, and have
        // lost access to the response headers on the original response.
        // We have to assume that loading the page as a vizportal resource
        // will get the user back to the intended redirect target.
        execute!(() => {
          const fragment = location.href.substring(response.url.length);
          redirect(`${location.origin}/#/${fragment}`, doc);
        });
        return;
      }
    }
    const loginRedirectUrl = response.headers.get("Location");
    const url = new URL(location.href);
    switch (response.status) {
      case 200: // Success, but the response body is empty
        break;
      case 400: // Bad Request
        void handleBadRequest(response, doc, stringsLoaded);
        return;
      case 401: // Unuathorized (semantically, more like 'Unauthenticated')
        if (loginRedirectUrl) {
          execute!(() => {
            if (
              url.searchParams.get(redirectGuard) === preventRedirectLoop &&
              new URL(
                new URL(loginRedirectUrl).searchParams.get("path") ?? "/",
                document.location.href
              ).searchParams.get(redirectGuard) === preventRedirectLoop
            ) {
              const tableauUserErrorMessage401 = response.headers.get(
                "tableau_user_error_message"
              );
              stringsLoaded
                .then((strings: TableauLocalizedStrings) => {
                  new ErrorDialog(doc)
                    .overwriteTitle(strings.getString("ServerErrorUnexpected"))
                    .overwriteBody(
                      tableauUserErrorMessage401 ||
                        strings.getString("ServerErrorUnknown")
                    )
                    .show();
                })
                .catch(() => {
                  console.log("Unable to load localized strings");
                  if (tableauUserErrorMessage401) {
                    new ErrorDialog(doc)
                      .overwriteBody(tableauUserErrorMessage401)
                      .show();
                  }
                });
            } else {
              redirect(loginRedirectUrl, doc);
            }
          });
          return;
        }
        break;
      case 403: // Forbidden
        void handleForbidden(response, doc, stringsLoaded);
        return;
      case 404: // Not Found
        void handleNotFound(response, doc, stringsLoaded);
        return;
      case 413: // Payload Too Large
        {
          const tableauUserErrorMessage413 = response.headers.get(
            "tableau_user_error_message"
          );
          try {
            const strings = await stringsLoaded;
            new ErrorDialog(doc)
              .overwriteTitle(
                strings.getString("ShowPageErrorResourceLimitExceededSummary")
              )
              .overwriteBodyHTML(
                tableauUserErrorMessage413 ||
                  strings.getString(
                    "ShowPageErrorResourceLimitExceededDetail"
                  ) +
                    "<br>" +
                    strings.getString("PressBackButtonMessage")
              )
              .show();
          } catch {
            console.log("Unable to load localized strings");
            if (tableauUserErrorMessage413) {
              new ErrorDialog(doc)
                .overwriteBodyHTML(tableauUserErrorMessage413)
                .show();
            }
          }
        }
        return;
      case 429: // Too Many Requests
        {
          const tableauUserErrorMessage429 = response.headers.get(
            "tableau_user_error_message"
          );
          try {
            const strings = await stringsLoaded;
            new ErrorDialog(doc)
              .overwriteTitle(strings.getString("RequestCountExceededTitle"))
              .overwriteBody(
                tableauUserErrorMessage429 ||
                  strings.getString("RequestCountExceededWarning")
              )
              .show();
          } catch {
            console.log("Unable to load localized strings");
            if (tableauUserErrorMessage429) {
              new ErrorDialog(doc)
                .overwriteBody(tableauUserErrorMessage429)
                .show();
            }
          }
        }
        return;
      case 502: // Bad Gateway
      case 503: // Service Unavailable
        void handleServiceUnavailable(
          response,
          doc,
          url,
          location,
          stringsLoaded,
          redirect,
          execute
        );

        return;
      default:
        console.error(`Unhandled response: ${response.status}`);
    }
    await showServerErrorDialog(response, stringsLoaded, doc);
  };
}

async function showServerErrorDialog(
  response: Response,
  stringsLoaded: Promise<TableauLocalizedStrings>,
  doc: Document
): Promise<void> {
  const error = await getExceptionJson(response);
  const tableauUserErrorMessage = response.headers.get(
    "tableau_user_error_message"
  );
  let strings: TableauLocalizedStrings;

  try {
    strings = await stringsLoaded;
  } catch {
    console.log("Unable to load localized strings");
    if (tableauUserErrorMessage) {
      new ErrorDialog(doc).overwriteBody(tableauUserErrorMessage).show();
    } else {
      new ErrorDialog(doc).show();
    }
    return;
  }

  if (error) {
    const details = error.details || [];
    const communityLink = error.communityLink || "";

    switch (error.type) {
      case "exception":
        showErrorPage(doc, strings, details, communityLink, error.errorType);
        return;
      default:
        break;
    }
  }

  new ErrorDialog(doc)
    .overwriteTitle(strings.getString("ServerErrorUnexpected"))
    .overwriteBody(
      tableauUserErrorMessage || strings.getString("ServerErrorUnknown")
    )
    .show();
}

function showInstantAnalyticsException(
  status: number,
  stringsLoaded: Promise<TableauLocalizedStrings>,
  doc: Document,
  pathname: string
): boolean {
  const exception = exceptionFrom(status);
  if (exception == null) return false;

  stringsLoaded
    .then((strings: TableauLocalizedStrings) => {
      showInstantAnalyticsErrorPageWithException(
        doc,
        strings,
        exception,
        pathname
      );
    })
    .catch(() =>
      console.error("Unable to display Instant Analytics error page.")
    );
  return true;
}
